/*
 * Author: Roger
 * File Created: Wednesday, 24-08-14 09:38 am
 * Last Modified: Wednesday, 25-06-04 04:50 pm, by Dw
 */

import { useCallback, useEffect } from 'react';

import { produce } from 'immer';
import { create } from 'zustand';
import { useShallow } from 'zustand/react/shallow';

import { dateSubtract, plus, toFixedFloor } from '@web/utils';

import type {
  Transaction,
  TransactionDetail,
  TransType,
  UserAssetDetail,
  UserNetworkAsset,
} from '@/@types/asset';
import type { Asset, Network, UserDepositAddress } from '@/@types/crypto';
import {
  getUserAssetDetail,
  getUserNetworkAssets,
  getUserTransactionDetail,
  getUserTransactions,
} from '@/services/asset.service';
import {
  getEnableAssets,
  getEnableNetworks,
  getUserDepositAddress,
} from '@/services/crypto.service';
import { canWithdraw } from '@/services/withdraw.service';
import logger from '@/utils/logger';
import { formatAssetNumber } from '@/utils/number';

import { useDynamicBalanceFunc } from './useCurrencyRatioStore';
import { useUserAsset } from './useUserAsset';
import { useUserInfo } from './useUserStore';

type DetailCache = Record<
  string,
  Record<string, Record<string, TransactionDetail>>
>;

type NotNullable<T> = T extends null | undefined ? never : T;

// const EXPIRED_TIME = 5 * 60 * 1000;

interface DvaultStoreState {
  pageSize: number;
  startDate: Record<number, number>; // 用于保存每个 dayLimit 对应的 start 时间戳
  endDate: Record<number, number>; // 用于保存每个 dayLimit 对应的 end 时间戳
  transDetailCache: DetailCache;
  assetCache: Record<string, Asset[]>; // { 'DEPOSIT': [], 'WITHDRAW': []}
  networkCache: Record<string, Record<string, Network[]>>; // { 'DEPOSIT': { 'USDT': []}, 'WITHDRAW': {'USDC': []}}
  latestNetworkCacheTime: number;
  userAssetCache?: UserNetworkAsset[];
  addressCache: Record<string, Record<string, UserDepositAddress>>;
  userAssetDetail?: UserAssetDetail;
  userCanWithdraw: Record<string, boolean>;
  /** 用来记录在dvault的时候，是否需要打开对应的充提弹窗 */
  modalActions?: {
    type: 'WITHDRAW' | 'DEPOSIT';
    deposit?: {
      asset: string;
      amount: string;
      /** 默认SGD */
      currency?: string;
      channel?: 'FOMOPAY';
    };
  };
}

interface DvaultStoreAction {
  clearData: () => void;
  fetchTransactions: (
    _dayLimit: number,
    _page: number,
    _force?: boolean,
    _pageSize?: number,
  ) => Promise<Transaction[]>;
  fetchTransDetail: (_item: {
    tranId: string;
    externalTranId: string;
    type: TransType;
  }) => Promise<TransactionDetail>;

  fetchAssets: (_type: TransType) => Promise<Asset[]>;
  fetchNetworks: (_type: TransType, _asset: string) => Promise<Network[]>;
  fetchUserAssets: () => Promise<UserNetworkAsset[]>;
  clearUserAssetCache: () => void;
  fetchUserAssetDetail: (_force?: boolean) => Promise<UserAssetDetail>;
  fetchUserAddress: (
    _asset: string,
    _network: string,
  ) => Promise<UserDepositAddress>;
  fetchUserCanWithdraw: (_asset: string) => Promise<boolean>;
  fetchUserEnableBehavior: (
    _asset: string,
  ) => Promise<{ withdrawEnable: boolean; depositEnable: boolean }>;
  clearModalActions: () => void;
  openDepositModal: (
    _p: NotNullable<DvaultStoreState['modalActions']>['deposit'],
  ) => void;
}

const DEFAULT_PAGE_SIZE = 10;

const defaultState: DvaultStoreState = {
  startDate: {},
  endDate: {},
  transDetailCache: {},
  userAssetDetail: undefined,
  addressCache: {},
  networkCache: {},
  latestNetworkCacheTime: 0,
  assetCache: {},
  userCanWithdraw: {},
  pageSize: DEFAULT_PAGE_SIZE,
};

export const useDvaultStore = create<DvaultStoreState & DvaultStoreAction>(
  (set, get) => ({
    isLoading: false,
    startDate: {},
    endDate: {},
    pageSize: DEFAULT_PAGE_SIZE,
    clearData: () => {
      set(() => defaultState);
    },
    fetchTransactions: async (
      dayLimit,
      page,
      force,
      _pageSize = DEFAULT_PAGE_SIZE,
    ): Promise<Transaction[]> => {
      const { startDate, endDate, pageSize } = get();

      let start: number;
      let end: number;

      // 检查是否已保存 start 和 end，如果没有则计算并保存
      if (!startDate[dayLimit] || !endDate[dayLimit] || (force && page === 1)) {
        if (dayLimit >= 30) {
          start = Date.UTC(2024, 6, 1, 0, 0, 0);
        } else {
          start = dateSubtract(Date.now(), dayLimit, 'day').valueOf();
        }

        end = Date.now() + 24 * 60 * 60 * 1000;
        set(
          produce((state: DvaultStoreState) => {
            state.startDate[dayLimit] = start;
            state.endDate[dayLimit] = end;
          }),
        );
      } else {
        start = startDate[dayLimit];
        end = endDate[dayLimit];
      }

      const { data } = await getUserTransactions({
        start,
        end,
        page,
        size: _pageSize || pageSize,
        force,
      });

      return data || [];
    },

    transDetailCache: {},
    fetchTransDetail: async (item: {
      tranId: string;
      externalTranId: string;
      type: TransType;
    }): Promise<TransactionDetail> => {
      const { transDetailCache } = get();
      const { tranId, externalTranId, type } = item;

      // 检查缓存
      if (transDetailCache[tranId]?.[externalTranId]?.[type]) {
        return transDetailCache[tranId][externalTranId][type];
      }

      const { data } = await getUserTransactionDetail(item);

      set(
        produce((state: DvaultStoreState) => {
          if (!state.transDetailCache[tranId]) {
            state.transDetailCache[tranId] = {};
          }

          if (!state.transDetailCache[tranId][externalTranId]) {
            state.transDetailCache[tranId][externalTranId] = {};
          }

          state.transDetailCache[tranId][externalTranId][type] = data;
        }),
      );

      return data;
    },

    assetCache: {},
    networkCache: {},
    userAssetCache: undefined,
    latestNetworkCacheTime: 0,
    fetchAssets: async (type: TransType) => {
      // const { assetCache } = get();

      // if (assetCache[type]) {
      //   return assetCache[type];
      // }

      try {
        const { data: assets } = await getEnableAssets({ type });

        set(
          produce((state: DvaultStoreState) => {
            state.assetCache[type] = assets;
          }),
        );

        return assets;
      } catch (error) {
        return [];
      }
    },
    fetchNetworks: async (type: TransType, asset: string) => {
      // const { networkCache, latestNetworkCacheTime } = get();
      // const expiredTime = Date.now() - EXPIRED_TIME;

      // network.fee expired after 10min, so force fetch data per 5min
      // if (networkCache[type]?.[asset] && latestNetworkCacheTime > expiredTime) {
      //   return networkCache[type][asset];
      // }

      try {
        const { data: networks } = await getEnableNetworks({ type, asset });

        set(
          produce((state: DvaultStoreState) => {
            if (!state.networkCache[type]) {
              state.networkCache[type] = {};
            }

            state.networkCache[type][asset] = networks;
          }),
        );

        return networks;
      } catch (error) {
        return [];
      }
    },
    fetchUserAssets: async () => {
      // const { userAssetCache } = get();

      // if (userAssetCache) {
      //   return userAssetCache;
      // }

      try {
        const { data: userAssets } = await getUserNetworkAssets();

        set(
          produce((state: DvaultStoreState) => {
            state.userAssetCache = userAssets;
          }),
        );

        return userAssets;
      } catch (error) {
        return [];
      }
    },
    clearUserAssetCache: () => {
      set(
        produce((state: DvaultStoreState) => {
          state.userAssetCache = undefined;
        }),
      );
    },

    addressCache: {},
    async fetchUserAddress(asset, network) {
      const { addressCache } = get();

      if (addressCache[asset]?.[network]) {
        return addressCache[asset][network];
      }

      const { data } = await getUserDepositAddress({ coin: asset, network });

      set(
        produce((state: DvaultStoreState) => {
          if (!state.addressCache[asset]) {
            state.addressCache[asset] = {};
          }

          state.addressCache[asset][network] = data;
        }),
      );

      return data;
    },

    userAssetDetail: undefined,
    fetchUserAssetDetail: async (force = false) => {
      const { userAssetDetail } = get();

      if (userAssetDetail && !force) {
        return userAssetDetail;
      }

      const { data } = await getUserAssetDetail();

      set(
        produce((state: DvaultStoreState) => {
          state.userAssetDetail = data;
        }),
      );

      return data;
    },

    userCanWithdraw: {},
    fetchUserCanWithdraw: async (asset) => {
      // const { userCanWithdraw } = get();

      // if (userCanWithdraw[asset] !== undefined) {
      //   return userCanWithdraw[asset];
      // }

      try {
        const res = await canWithdraw({ asset });

        set(
          produce((state: DvaultStoreState) => {
            state.userCanWithdraw[asset] = res.data;
          }),
        );

        return res.data;
      } catch (error) {
        return false;
      }
    },

    fetchUserEnableBehavior: async (asset) => {
      const behavior = { withdrawEnable: false, depositEnable: false };
      const promises = [
        get().fetchUserCanWithdraw(asset),
        get().fetchNetworks('WITHDRAW', asset),
        get().fetchNetworks('DEPOSIT', asset),
      ];
      const [_canWithdraw, _withdrawNetworks, _depositNetworks] =
        await Promise.all(promises);

      if (_canWithdraw) {
        behavior.withdrawEnable =
          Array.isArray(_withdrawNetworks) &&
          _withdrawNetworks.some((item) => item.withdrawEnable);
      }

      behavior.depositEnable =
        Array.isArray(_depositNetworks) &&
        _depositNetworks.some((item) => item.depositEnable);

      return behavior;
    },

    clearModalActions: () => {
      set(
        produce((state: DvaultStoreState) => {
          state.modalActions = undefined;
        }),
      );
    },

    openDepositModal: (params) => {
      set(
        produce((state: DvaultStoreState) => {
          state.modalActions = {
            type: 'DEPOSIT',
            deposit: params,
          };
        }),
      );
    },
  }),
);

let isFetchingUserAsset = false;

export const useUserBalanceVal = (params?: { noAddtionReq?: boolean }) => {
  const { noAddtionReq = false } = params || {};
  const { isPrivacy, selectedCurrency } = useUserAsset([
    'isPrivacy',
    'selectedCurrency',
  ]);
  const { isLogin } = useUserInfo();
  const { getAmount } = useDynamicBalanceFunc();

  const { userAssetDetail, fetchUserAssetDetail } = useDvaultStore(
    useShallow((state) => ({
      userAssetDetail: state.userAssetDetail,
      fetchUserAssetDetail: state.fetchUserAssetDetail,
    })),
  );

  useEffect(() => {
    if (!isLogin) return;

    if (noAddtionReq && userAssetDetail?.totalUsd !== undefined) {
      return;
    }

    if (isFetchingUserAsset) {
      return;
    }

    isFetchingUserAsset = true;

    fetchUserAssetDetail(true)
      .catch(() => {
        // noop
      })
      .finally(() => {
        isFetchingUserAsset = false;
      });
  }, [userAssetDetail, isLogin]);

  if (isPrivacy) {
    return '****';
  }

  return formatAssetNumber(
    toFixedFloor(
      userAssetDetail?.userAssets?.reduce(
        (acc, item) => {
          if (item.fiat && selectedCurrency === 'SGD') {
            return plus(acc, Number(toFixedFloor(item.total ?? '', 2)));
          }

          return plus(acc, Number(getAmount(item.totalUsd)));
        },
        plus(0, 0),
      ) || 0,
      2,
      false,
    ),
  );
};

export const useUserAssetDetail = () => {
  const { userAssetDetail, fetchUserAssetDetail } = useDvaultStore(
    useShallow((state) => ({
      userAssetDetail: state.userAssetDetail,
      fetchUserAssetDetail: state.fetchUserAssetDetail,
    })),
  );

  const { isLogin } = useUserInfo();

  const fetchDetailData = useCallback(async () => {
    try {
      await fetchUserAssetDetail(true);
    } catch (error) {
      logger.error('Fail to get UserAssetDetail: ', error);
    }
  }, [fetchUserAssetDetail]);

  useEffect(() => {
    if (isLogin) {
      void fetchDetailData();
    }
  }, [isLogin, fetchDetailData]);

  return userAssetDetail;
};
